import { useEffect, useState } from 'react'
import { supabase } from '../supabaseClient'
import { useParams, useNavigate } from 'react-router-dom'
import Navbar from '../components/Navbar'

export default function ComplianceDashboard() {
  const { patientId } = useParams()
  const navigate = useNavigate()
  const [patient, setPatient] = useState(null)
  const [assignments, setAssignments] = useState([])
  const [taskAssignments, setTaskAssignments] = useState([])
  const [completions, setCompletions] = useState([])
  const [taskCompletions, setTaskCompletions] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchData()
    const channel = supabase
      .channel('compliance-live')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'completions', filter: 'patient_id=eq.' + patientId }, payload => {
        if (payload.eventType === 'INSERT') setCompletions(prev => [...prev, payload.new])
        else if (payload.eventType === 'DELETE') setCompletions(prev => prev.filter(c => c.id !== payload.old.id))
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'task_completions', filter: 'patient_id=eq.' + patientId }, payload => {
        if (payload.eventType === 'INSERT') setTaskCompletions(prev => [...prev, payload.new])
        else if (payload.eventType === 'DELETE') setTaskCompletions(prev => prev.filter(c => c.id !== payload.old.id))
      })
      .subscribe()
    return () => supabase.removeChannel(channel)
  }, [])

  async function fetchData() {
    const thirtyDaysAgo = new Date()
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30)
    const from = thirtyDaysAgo.toISOString().split('T')[0]
    const [{ data: patientData }, { data: assignmentData }, { data: taskAssignmentData }, { data: completionData }, { data: taskCompletionData }] = await Promise.all([
      supabase.from('users').select('*').eq('id', patientId).single(),
      supabase.from('assignments').select('*, exercises(*)').eq('patient_id', patientId).order('sort_order'),
      supabase.from('intervention_assignments').select('*, tasks(*)').eq('patient_id', patientId).order('sort_order'),
      supabase.from('completions').select('*').eq('patient_id', patientId).gte('completed_on', from),
      supabase.from('intervention_completions').select('*').eq('patient_id', patientId).gte('completed_on', from)
    ])
    setPatient(patientData)
    setAssignments(assignmentData || [])
    setTaskAssignments(taskAssignmentData || [])
    setCompletions(completionData || [])
    setTaskCompletions(taskCompletionData || [])
    setLoading(false)
  }

  function getLast30Days() {
    return Array.from({ length: 30 }, (_, i) => {
      const d = new Date()
      d.setDate(d.getDate() - (29 - i))
      return d.toISOString().split('T')[0]
    })
  }

  const today = new Date().toISOString().split('T')[0]
  const days = getLast30Days()
  const [selectedDate, setSelectedDate] = useState(today)

  function shiftDate(offset) {
    const d = new Date(selectedDate + 'T00:00:00')
    d.setDate(d.getDate() + offset)
    const str = d.toISOString().split('T')[0]
    if (str <= today) setSelectedDate(str)
  }

  const todayEx = completions.filter(c => c.completed_on === selectedDate).length
  const todayIv = taskCompletions.filter(c => c.completed_on === selectedDate).length
  const exRate = assignments.length ? Math.round((completions.length / (assignments.length * 30)) * 100) : 0
  const ivRate = taskAssignments.length ? Math.round((taskCompletions.length / (taskAssignments.length * 30)) * 100) : 0

  if (loading) return <><Navbar /><div className="page"><p>Loading...</p></div></>

  return (
    <>
      <Navbar />
      <div className="page">
        <span onClick={() => navigate('/clinician/patients/' + patientId)} className="back-link" style={{ cursor: 'pointer' }}>← Back to program</span>
        <div className="page-header">
          <h1>{patient?.full_name}</h1>
          <p>Compliance — last 30 days</p>
        </div>

        {/* Summary stats row */}
        <div style={{ display: 'grid', gridTemplateColumns: assignments.length && taskAssignments.length ? '1fr 1fr' : '1fr', gap: 12, marginBottom: '1.5rem' }}>
          {assignments.length > 0 && (
            <div className="card" style={{ background: 'linear-gradient(135deg, #6366f1, #4f46e5)', border: 'none' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, margin: '0 0 6px' }}>🏋️ Exercises</p>
                  <p style={{ color: 'white', fontSize: 28, fontWeight: 800, margin: 0, letterSpacing: -1 }}>
                    {exRate}<span style={{ fontSize: 16, opacity: 0.6 }}>%</span>
                  </p>
                  <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: 12, margin: '4px 0 0' }}>30-day rate</p>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: 11, margin: '0 0 2px' }}>{selectedDate === today ? 'Today' : new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</p>
                  <p style={{ color: 'white', fontSize: 18, fontWeight: 700, margin: 0 }}>{todayEx}/{assignments.length}</p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, justifyContent: 'flex-end', marginTop: 4 }}>
                    <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#4ade80' }} />
                    <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: 11, margin: 0 }}>Live</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {taskAssignments.length > 0 && (
            <div className="card" style={{ background: 'linear-gradient(135deg, #0f766e, #0d9488)', border: 'none' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, margin: '0 0 6px' }}>🩺 Tasks</p>
                  <p style={{ color: 'white', fontSize: 28, fontWeight: 800, margin: 0, letterSpacing: -1 }}>
                    {ivRate}<span style={{ fontSize: 16, opacity: 0.6 }}>%</span>
                  </p>
                  <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: 12, margin: '4px 0 0' }}>30-day rate</p>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: 11, margin: '0 0 2px' }}>{selectedDate === today ? 'Today' : new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</p>
                  <p style={{ color: 'white', fontSize: 18, fontWeight: 700, margin: 0 }}>{todayIv}/{taskAssignments.length}</p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, justifyContent: 'flex-end', marginTop: 4 }}>
                    <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#4ade80' }} />
                    <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: 11, margin: 0 }}>Live</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Heatmaps */}
        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <h2 style={{ fontSize: 15, marginBottom: '1.25rem' }}>30-day heatmap</h2>

          {assignments.length > 0 && (
            <div style={{ marginBottom: taskAssignments.length > 0 ? '1.25rem' : 0 }}>
              {taskAssignments.length > 0 && (
                <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: 0.5, margin: '0 0 8px' }}>🏋️ Exercises</p>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(10, 1fr)', gap: 5 }}>
                {days.map(day => {
                  const rate = assignments.length ? completions.filter(c => c.completed_on === day).length / assignments.length : 0
                  return (
                    <div key={day} title={day + ': ' + Math.round(rate * 100) + '%'} onClick={() => setSelectedDate(day)} style={{
                      aspectRatio: '1', borderRadius: 5, cursor: 'pointer',
                      background: rate === 0 ? 'var(--border)' : rate < 0.5 ? '#86efac' : rate < 1 ? '#22c55e' : '#16a34a',
                      outline: day === selectedDate ? '2px solid #6366f1' : day === today ? '2px solid #94a3b8' : 'none', outlineOffset: 2
                    }} />
                  )
                })}
              </div>
            </div>
          )}

          {taskAssignments.length > 0 && (
            <div>
              {assignments.length > 0 && (
                <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: 0.5, margin: '0 0 8px' }}>🩺 Tasks</p>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(10, 1fr)', gap: 5 }}>
                {days.map(day => {
                  const rate = taskAssignments.length ? taskCompletions.filter(c => c.completed_on === day).length / taskAssignments.length : 0
                  return (
                    <div key={day} title={day + ': ' + Math.round(rate * 100) + '%'} onClick={() => setSelectedDate(day)} style={{
                      aspectRatio: '1', borderRadius: 5, cursor: 'pointer',
                      background: rate === 0 ? 'var(--border)' : rate < 0.5 ? '#86efac' : rate < 1 ? '#22c55e' : '#16a34a',
                      outline: day === selectedDate ? '2px solid #6366f1' : day === today ? '2px solid #94a3b8' : 'none', outlineOffset: 2
                    }} />
                  )
                })}
              </div>
            </div>
          )}

          <div style={{ display: 'flex', gap: 12, marginTop: 12, alignItems: 'center' }}>
            <p style={{ fontSize: 11, color: 'var(--text-tertiary)', margin: 0 }}>Less</p>
            {['var(--border)', '#86efac', '#22c55e', '#16a34a'].map(c => (
              <div key={c} style={{ width: 11, height: 11, borderRadius: 3, background: c }} />
            ))}
            <p style={{ fontSize: 11, color: 'var(--text-tertiary)', margin: 0 }}>More</p>
          </div>
        </div>

        {/* Day breakdown */}
        <div className="card">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
            <h2 style={{ fontSize: 15, margin: 0 }}>
              {selectedDate === today ? "Today's breakdown" : new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
            </h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <button type="button" onClick={() => shiftDate(-1)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '4px 8px', borderRadius: 6, color: 'var(--text-secondary)', fontSize: 18, lineHeight: 1 }}>‹</button>
              <button type="button" onClick={() => shiftDate(1)} disabled={selectedDate === today} style={{ background: 'transparent', border: 'none', cursor: selectedDate === today ? 'default' : 'pointer', padding: '4px 8px', borderRadius: 6, color: selectedDate === today ? 'var(--border)' : 'var(--text-secondary)', fontSize: 18, lineHeight: 1 }}>›</button>
            </div>
          </div>

          {assignments.length === 0 && taskAssignments.length === 0 && (
            <p>Nothing assigned yet.</p>
          )}

          {assignments.length > 0 && (
            <div style={{ marginBottom: taskAssignments.length > 0 ? '1.25rem' : 0 }}>
              {taskAssignments.length > 0 && (
                <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: 0.5, margin: '0 0 8px' }}>Exercises</p>
              )}
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {assignments.map((assignment, i) => {
                  const done = completions.some(c => c.assignment_id === assignment.id && c.completed_on === selectedDate)
                  return (
                    <div key={assignment.id} style={{
                      display: 'flex', alignItems: 'center', gap: 12,
                      padding: '10px 0',
                      borderBottom: i < assignments.length - 1 ? '1px solid var(--border)' : 'none'
                    }}>
                      <div style={{
                        width: 20, height: 20, borderRadius: 5, flexShrink: 0,
                        background: done ? 'var(--success)' : 'var(--bg)',
                        border: '1.5px solid ' + (done ? 'var(--success)' : 'var(--border-strong)'),
                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                      }}>
                        {done && <span style={{ color: 'white', fontSize: 11, fontWeight: 700 }}>✓</span>}
                      </div>
                      <p style={{ margin: 0, fontSize: 14, flex: 1, color: done ? 'var(--success)' : 'var(--text-primary)', fontWeight: done ? 500 : 400 }}>
                        {assignment.exercises.title}
                      </p>
                      {done && <span className="badge badge-success" style={{ fontSize: 11 }}>Done</span>}
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {taskAssignments.length > 0 && (
            <div>
              {assignments.length > 0 && (
                <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: 0.5, margin: '0 0 8px' }}>Tasks</p>
              )}
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {taskAssignments.map((assignment, i) => {
                  const done = taskCompletions.some(c => c.task_assignment_id === assignment.id && c.completed_on === selectedDate)
                  return (
                    <div key={assignment.id} style={{
                      display: 'flex', alignItems: 'center', gap: 12,
                      padding: '10px 0',
                      borderBottom: i < taskAssignments.length - 1 ? '1px solid var(--border)' : 'none'
                    }}>
                      <div style={{
                        width: 20, height: 20, borderRadius: 5, flexShrink: 0,
                        background: done ? '#0d9488' : 'var(--bg)',
                        border: '1.5px solid ' + (done ? '#0d9488' : 'var(--border-strong)'),
                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                      }}>
                        {done && <span style={{ color: 'white', fontSize: 11, fontWeight: 700 }}>✓</span>}
                      </div>
                      <p style={{ margin: 0, fontSize: 14, flex: 1, color: done ? '#0d9488' : 'var(--text-primary)', fontWeight: done ? 500 : 400 }}>
                        {assignment.tasks.title}
                      </p>
                      {done && <span style={{ fontSize: 11, fontWeight: 600, color: '#0d9488', background: '#f0fdf4', padding: '3px 8px', borderRadius: 99 }}>Done</span>}
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  )
}
